//@flow
import {
    pjpMasterApi,
    bulkPjpFetchApi,
    pjpMasterOutletsApi,
    pjpMasterRidersApi,
    pjpIncrementalPlan as pjpIncrementalPlanApi,
    validateEntityIds as validateEntityIdsApi
} from 'common/middleware/api';
import PaginatedEntityActions from 'common/redux/actions/PaginatedEntityActions';
import { showToast } from 'common/redux/actions/toast';
import OutletActions from 'common/redux/actions/outlet';
import RiderActions from 'common/redux/actions/rider';
import { ToastTypes } from 'common/components/core/toast/Toast';
import I18nHelper from 'common/helpers/I18nHelper';
import { PermanentJourneyPlan } from 'common/models/internal/PermanentJourneyPlan';
import PjpSelectors from 'common/redux/selectors/pjpMaster';
import { injectError } from 'common/redux/actions/error';
import type { Dispatch } from 'redux';
import { LocusApiModel } from 'common/modules/api';
import type { FetchEntitiesOptionsType } from 'common/redux/actions/BaseActions';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

type Params = {
    clientId: string,
    pjpId?: string
};

const ENTITY_NAME_ID = 'masterPjp';
const PJP_REFRESH_INTERVAL = 30 * 1000; // 30 seconds

class PjpActions extends PaginatedEntityActions<PermanentJourneyPlan, Params> {
    constructor(
        TAG: string,
        api: LocusApiModel,
        bulkFetchApi: LocusApiModel,
        pjpMasterOutletsApi: LocusApiModel,
        pjpMasterRidersApi: LocusApiModel
    ) {
        super(TAG, api, bulkFetchApi);
        this.pjpMasterOutletsApi = pjpMasterOutletsApi;
        this.pjpMasterRidersApi = pjpMasterRidersApi;
    }

    /*
        1. Start polling in progress plans
        2. Logic to continue polling
        3. Logic to stop polling once no longer in progress
        4. Logic to stop polling on page change/navigation
    */
    startPollingInProgressPjps(entities: Object) {
        return (dispatch: Dispatch<*>) => {
            entities.pjps.forEach(pjp => {
                // We are doing this fromJS conversion twice on fetchEntities actions now, one here,
                // and one in the reducer. Need a better way to do this.
                // Probably a beforeInject lifecycle hook in reducer?
                const pjpEntity = PermanentJourneyPlan.fromJS(pjp);
                if (pjpEntity.isInProgress()) {
                    dispatch(this.refreshPjpTillCompletion(pjp));
                }
            });
        };
    }

    refreshPjpTillCompletion(pjp: PermanentJourneyPlan) {
        return (dispatch: Dispatch<*>, getState: () => Object) => {
            const { clientId, id: pjpId } = pjp;
            const params = { clientId, pjpId };
            const successHandler = (plainEntity: Object) => {
                const pjpEntity = PermanentJourneyPlan.fromJS(plainEntity);
                if (pjpEntity.isInProgress()) {
                    dispatch(this.refreshPjpTillCompletion(pjpEntity));
                }
                // Else, no issues. No more polling.
            };
            setTimeout(() => {
                // We need to double check the polling is still required,
                // as the user might have moved away from the page or screen
                const entities = PjpSelectors.getEntitiesForClient(
                    PjpSelectors.storeSelector(getState()),
                    pjp.clientId
                );
                if (!entities) {
                    // No longer relevant. Probably moved away from the page
                    return;
                }
                const entity = PjpSelectors.getEntityFromEntityId(
                    entities,
                    pjp.id
                );
                if (!entity || !entity.isInProgress()) {
                    // Its no longer a relevant entity, or someone else injected a completed entity.
                    return;
                }
                dispatch(
                    this.refreshEntity(params, { onSuccess: successHandler })
                );
            }, PJP_REFRESH_INTERVAL);
        };
    }

    superFetchEntities = this.fetchEntities;
    fetchEntities = (
        params: Params,
        body: Object,
        options: FetchEntitiesOptionsType = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            const newOptions = {
                ...options,
                onSuccess: (entities: Object) => {
                    dispatch(this.startPollingInProgressPjps(entities));
                    options.onSuccess && options.onSuccess(entities);
                },
                onFailure: (error: Error) => {
                    options.onFailure && options.onFailure(error);
                }
            };
            dispatch(this.superFetchEntities(params, body, newOptions));
        };
    };

    superAddEntities = this.addEntities;
    addEntities = (
        params: { clientId: string },
        entity: PermanentJourneyPlan,
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (pjp: PermanentJourneyPlan) => {
                        const toast = {
                            message: nsTranslate('addEntitySuccessMessage', {
                                entityNameId: ENTITY_NAME_ID
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(pjp);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                dispatch(this.superAddEntities(params, entity, options));
            });
        };
    };

    fetchPjpOutletsCall = (params, body, options = {}) => {
        const { processResponse, onSuccess } = options;

        return this.pjpMasterOutletsApi.post(params, body).then(response => {
            let entities = null;
            if (processResponse) {
                entities = processResponse(response);
            } else {
                entities = (response: Array<Object>);
            }
            onSuccess && onSuccess(entities);
            return { response, entities };
        });
    };

    fetchPjpOutlets = (params, body, options = {}) => {
        return (dispatch: Dispatch<*>) => {
            dispatch(OutletActions.emitFetchEntitiesStart(params, body));
            this.fetchPjpOutletsCall(params, body, options)
                .then(({ entities }) => {
                    dispatch(
                        OutletActions.injectEntities(params, entities, body)
                    );
                })
                .catch((error: Error) => {
                    dispatch(
                        OutletActions.emitFetchEntitiesFailure(
                            params,
                            error,
                            body
                        )
                    );
                    OutletActions.handleError(error, options, dispatch);
                });
        };
    };

    fetchPjpRidersCall = (params, body, options = {}) => {
        const { processResponse, onSuccess } = options;
        return this.pjpMasterRidersApi.post(params, body).then(response => {
            let entities = null;
            if (processResponse) {
                entities = processResponse(response);
            } else {
                entities = (response: Array<Object>);
            }
            onSuccess && onSuccess(entities);
            return { response, entities };
        });
    };

    fetchPjpRiders = (params, body, options = {}) => {
        return (dispatch: Dispatch<*>) => {
            dispatch(RiderActions.emitFetchEntitiesStart(params, body));
            this.fetchPjpRidersCall(params, body, options)
                .then(({ entities }) => {
                    dispatch(
                        RiderActions.injectEntities(params, entities, body)
                    );
                })
                .catch((error: Error) => {
                    dispatch(
                        RiderActions.emitFetchEntitiesFailure(
                            params,
                            error,
                            body
                        )
                    );
                    RiderActions.handleError(error, options, dispatch);
                });
        };
    };

    superPatchEntity = this.patchEntity;

    patchEntity = (
        params: { clientId: string, pjpId: string },
        oldEntity: PermanentJourneyPlan,
        newEntity: PermanentJourneyPlan
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (pjp: PermanentJourneyPlan) => {
                        const toast = {
                            message: nsTranslate('savedChangesMessage'),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(pjp);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                dispatch(
                    this.superPatchEntity(params, oldEntity, newEntity, options)
                );
            });
        };
    };

    recursiveFetchEntities = (
        fetchEntitiesCall,
        entityType,
        params,
        body,
        { resolve, reject, options },
        { prevEntities = [], page = 1 } = {}
    ) => {
        fetchEntitiesCall(
            params,
            { ...body, page, size: 100 },
            {
                ...options,
                injectEntities: false,
                onSuccess: entities => {
                    const nextEntites = prevEntities.concat(
                        entities[entityType]
                    );
                    if (page === entities.paginationInfo.numberOfPages) {
                        resolve({
                            entities: {
                                entities: nextEntites,
                                paginationInfo: entities.paginationInfo
                            }
                        });
                    } else {
                        this.recursiveFetchEntities(
                            fetchEntitiesCall,
                            entityType,
                            params,
                            body,
                            {
                                resolve,
                                reject,
                                options
                            },
                            {
                                prevEntities: nextEntites,
                                page: entities.paginationInfo.currentPage + 1
                            }
                        );
                    }
                },
                onFailure: error => {
                    reject(error);
                }
            }
        );
    };

    fetchAllActiveOutlets = (params, body, options) => {
        return new Promise((resolve, reject) => {
            this.recursiveFetchEntities(
                OutletActions.fetchEntitiesCall,
                'outlets',
                params,
                body,
                {
                    resolve,
                    reject,
                    options
                }
            );
        });
    };

    fetchAllActiveRiders = (params, body, options) => {
        return new Promise((resolve, reject) => {
            this.recursiveFetchEntities(
                RiderActions.fetchEntitiesCall,
                'riders',
                params,
                body,
                {
                    resolve,
                    reject,
                    options
                }
            );
        });
    };

    createPjpIncrementalPlan = (params, body, options) => {
        return (dispatch: Dispatch<*>) => {
            const { onSuccess, onFailure } = options;
            return new Promise((resolve, reject) => {
                pjpIncrementalPlanApi
                    .post(params, body.toJS())
                    .then(response => {
                        resolve(response);
                        onSuccess && onSuccess(response);
                    })
                    .catch(error => {
                        onFailure && onFailure(error);
                        reject(error);
                        dispatch(injectError(error));
                    });
            });
        };
    };
}

export default new PjpActions(
    'PJP',
    pjpMasterApi,
    bulkPjpFetchApi,
    pjpMasterOutletsApi,
    pjpMasterRidersApi,
    validateEntityIdsApi
);
